import InitApi from "api/InitApi";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import useTokenStore from "store/tokenStore";
import { fetchConfig } from "utils";

export const DEFAULT_ROWS_PER_PAGE = 10;

export const readTagPayload = (response) => {
	const payload = response?.data?.data;

	if (Array.isArray(payload)) {
		return {
			records: payload,
			currentPage: 1,
			totalPages: 1,
			totalRecords: payload.length,
			rowsPerPage: payload.length || DEFAULT_ROWS_PER_PAGE,
			hasMorePages: false,
		};
	}

	return {
		records: payload?.records || [],
		currentPage: payload?.current_page || 1,
		totalPages: payload?.total_pages || 1,
		totalRecords: payload?.total_records || 0,
		rowsPerPage: payload?.rows_per_page || DEFAULT_ROWS_PER_PAGE,
		hasMorePages: Boolean(payload?.has_more_pages),
		usedCount: payload?.used_count,
		unusedCount: payload?.unused_count,
	};
};

export const useDebouncedValue = (value, delay = 300) => {
	const [debounced, setDebounced] = useState(value);

	useEffect(() => {
		const timer = setTimeout(() => setDebounced(value), delay);
		return () => clearTimeout(timer);
	}, [value, delay]);

	return debounced;
};

export const tagsQueryKey = (
	fromPortal = "no",
	{ search = "", page = 1, rowsPerPage = DEFAULT_ROWS_PER_PAGE, sort = "name", usage = "all" } = {},
) => ["tags", fromPortal, search, page, rowsPerPage, sort, usage];

export const prefetchTags = (queryClient, accessToken, fromPortal = "no", options = {}) => {
	const {
		search = "",
		page = 1,
		rowsPerPage = DEFAULT_ROWS_PER_PAGE,
		sort = "name",
		usage = "all",
	} = options;

	return queryClient.prefetchQuery(
		tagsQueryKey(fromPortal, { search, page, rowsPerPage, sort, usage }),
		async () => {
			const instance = await InitApi({ accessToken });
			const params = {
				from_portal: fromPortal,
				page,
				rows_per_page: rowsPerPage,
			};
			if (search) params.search = search;
			if (sort && sort !== "name") params.sort = sort;
			if (usage && usage !== "all") params.usage = usage;
			return instance.get("book-tags", { params });
		},
	);
};

const useTags = (fromPortal = "no", searchOrOptions = "") => {
	const options =
		typeof searchOrOptions === "string" ? { search: searchOrOptions } : searchOrOptions || {};
	const {
		search = "",
		page = 1,
		rowsPerPage = DEFAULT_ROWS_PER_PAGE,
		sort = "name",
		usage = "all",
		enabled = true,
	} = options;
	const { accessToken } = useTokenStore();

	const fetch = async () => {
		const instance = await InitApi({ accessToken });
		const params = {
			from_portal: fromPortal,
			page,
			rows_per_page: rowsPerPage,
		};
		if (search) params.search = search;
		if (sort && sort !== "name") params.sort = sort;
		if (usage && usage !== "all") params.usage = usage;
		const response = await instance.get("book-tags", { params });
		return response;
	};

	return useQuery(
		tagsQueryKey(fromPortal, { search, page, rowsPerPage, sort, usage }),
		() => fetch(),
		{
			...fetchConfig,
			keepPreviousData: true,
			enabled,
		},
	);
};

export const usePagedTags = ({
	fromPortal = "no",
	search = "",
	rowsPerPage = DEFAULT_ROWS_PER_PAGE,
	sort = "name",
	usage = "all",
	enabled = true,
} = {}) => {
	const debouncedSearch = useDebouncedValue(search);
	const [page, setPage] = useState(1);
	const [records, setRecords] = useState([]);
	const loadingMoreRef = useRef(false);
	const query = useTags(fromPortal, {
		search: debouncedSearch,
		page,
		rowsPerPage,
		sort,
		usage,
		enabled,
	});
	const payload = readTagPayload(query.data);

	useEffect(() => {
		setPage(1);
		setRecords([]);
		loadingMoreRef.current = false;
	}, [debouncedSearch, fromPortal, rowsPerPage, sort, usage]);

	useEffect(() => {
		if (!query.isFetching) {
			loadingMoreRef.current = false;
		}
	}, [query.isFetching]);

	useEffect(() => {
		if (!query.data || query.isFetching) return undefined;
		const next = readTagPayload(query.data);
		setRecords((current) => {
			if (next.currentPage <= 1) return next.records;
			const known = new Set(current.map((tag) => tag.id));
			return [...current, ...next.records.filter((tag) => !known.has(tag.id))];
		});
		return undefined;
	}, [query.data, query.isFetching]);

	return {
		records,
		hasMorePages: payload.hasMorePages,
		totalRecords: payload.totalRecords,
		isLoading: query.isLoading,
		isFetching: query.isFetching,
		// True only while fetching page 2+ — not on initial load or background refetch/prefetch.
		isLoadingMore: query.isFetching && page > 1,
		loadMore: () => {
			if (!payload.hasMorePages || query.isFetching || loadingMoreRef.current) {
				return;
			}
			loadingMoreRef.current = true;
			setPage((current) => current + 1);
		},
	};
};

export default useTags;
